import React, { useEffect } from 'react';
import DragDropUpload from "../components/DragDropUpload";

const Step2Address = ({ 
  step2Data, 
  handleStep2Change, 
  validateStep2, 
  submitStep2Data,
  loadingStates,
  previousStep, 
  nextStep, 
  validationErrors, 
  setValidationErrors,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  getDocumentCardState,
  handleDocumentClick,
  resetAllUploadingStates,
  documentErrors,
  setDocumentErrors,
  selectedDocument,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleClearSelection,
  handleUploadStart,
  lockedFields = {}
}) => {

  // Provide default for lockedFields
  const locked = lockedFields || {};

  // Automatically set country to South Africa when component mounts
  useEffect(() => {
    if (!step2Data.country || step2Data.country !== 'South Africa') {
      handleStep2Change('country', 'South Africa');
    }
  }, [step2Data.country, handleStep2Change]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Step2 handleSubmit called');
    console.log('Step2 data:', step2Data);
    console.log('Uploaded documents:', uploadedDocuments);
    console.log('Has proof_of_address:', uploadedDocuments.has('proof_of_address'));
    
    // First validate that the required document is uploaded
    if (!uploadedDocuments.has('proof_of_address')) {
      console.log('Proof of address document missing, setting error and scrolling');
      // Set document error to show the missing document alert
      setDocumentErrors(new Set(['proof_of_address']));
      
      // Scroll to the document section
      document.querySelector('[data-document="proof_of_address"]')?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      return; // Don't proceed to next step
    }
    
    console.log('Document validation passed, proceeding to submit step2 data');
    // Clear any document errors if validation passes
    setDocumentErrors(new Set());
    
    try {
      const success = await submitStep2Data();
      console.log('submitStep2Data result:', success);
      if (success) {
        console.log('Step2 submission successful, proceeding to next step');
        nextStep();
      } else {
        console.log('Step2 submission failed');
      }
    } catch (error) {
      console.error('Error submitting Step2 data:', error);
    }
  };

  const handlePrevious = () => {
    // Clear any validation errors when going back
    setValidationErrors({});
    previousStep();
  };

  return (
    <div className="container">
      <form onSubmit={handleSubmit}>
        <h3>Current Address</h3>

        {/* Country - Full width - Locked to South Africa */}
        <div className="form-floating mb-3">
          <input
            className="form-control"
            type="text"
            id="countryInput"
            value="South Africa"
            readOnly
            style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
          />
          <label htmlFor="countryInput">Country</label>
        </div>

        {/* Province/State - Full width - South African Provinces */}
        <div className="form-floating mb-3">
          <select
            className={`form-select ${validationErrors.province ? 'is-invalid' : ''}`}
            id="provinceInput"
            value={step2Data.province}
            onChange={(e) => handleStep2Change('province', e.target.value)}
            disabled={locked.province}
          >
            <option value="">Select Province</option>
            <option value="Eastern Cape">Eastern Cape</option>
            <option value="Free State">Free State</option>
            <option value="Gauteng">Gauteng</option>
            <option value="KwaZulu-Natal">KwaZulu-Natal</option>
            <option value="Limpopo">Limpopo</option>
            <option value="Mpumalanga">Mpumalanga</option>
            <option value="Northern Cape">Northern Cape</option>
            <option value="North West">North West</option>
            <option value="Western Cape">Western Cape</option>
          </select>
          <label htmlFor="provinceInput">Province</label>
          {validationErrors.province && (
            <div className="invalid-feedback">{validationErrors.province}</div>
          )}
        </div>

        {/* Address Line 1 and Address Line 2 - Horizontally aligned */}
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="form-floating">
              <input
                className={`form-control ${validationErrors.address_line_1 ? 'is-invalid' : ''}`}
                type="text"
                id="addressLine1Input"
                placeholder="Address Line 1"
                value={step2Data.address_line_1 || ''}
                onChange={(e) => handleStep2Change('address_line_1', e.target.value)}
                disabled={locked.address_line_1}
              />
              <label htmlFor="addressLine1Input">Address Line 1</label>
              {validationErrors.address_line_1 && (
                <div className="invalid-feedback">{validationErrors.address_line_1}</div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-floating">
              <input
                className={`form-control ${validationErrors.address_line_2 ? 'is-invalid' : ''}`}
                type="text"
                id="addressLine2Input"
                placeholder="Address Line 2 (Optional)"
                value={step2Data.address_line_2 || ''}
                onChange={(e) => handleStep2Change('address_line_2', e.target.value)}
              />
              <label htmlFor="addressLine2Input">Address Line 2 (Optional)</label>
              {validationErrors.address_line_2 && (
                <div className="invalid-feedback">{validationErrors.address_line_2}</div>
              )}
            </div>
          </div>
        </div>

        {/* Suburb and City - Horizontally aligned */}
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="form-floating">
              <input
                className={`form-control ${validationErrors.suburb ? 'is-invalid' : ''}`}
                type="text"
                id="suburbInput"
                placeholder="Suburb"
                value={step2Data.suburb || ''}
                onChange={(e) => handleStep2Change('suburb', e.target.value)}
                disabled={locked.suburb}
              />
              <label htmlFor="suburbInput">Suburb</label>
              {validationErrors.suburb && (
                <div className="invalid-feedback">{validationErrors.suburb}</div>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-floating">
              <input
                className={`form-control ${validationErrors.city ? 'is-invalid' : ''}`}
                type="text"
                id="cityInput"
                placeholder="City"
                value={step2Data.city}
                onChange={(e) => handleStep2Change('city', e.target.value)}
                disabled={locked.city}
              />
              <label htmlFor="cityInput">City</label>
              {validationErrors.city && (
                <div className="invalid-feedback">{validationErrors.city}</div>
              )}
            </div>
          </div>
        </div>

        {/* Postcode - Full width */}
        <div className="form-floating mb-3">
          <input
            className={`form-control ${validationErrors.postcode ? 'is-invalid' : ''}`}
            type="text"
            id="postcodeInput"
            placeholder="Postal code/Zip"
            value={step2Data.postcode}
            onChange={(e) => handleStep2Change('postcode', e.target.value)}
            disabled={locked.postcode}
          />
          <label htmlFor="postcodeInput">Postal code/Zip</label>
          {validationErrors.postcode && (
            <div className="invalid-feedback">{validationErrors.postcode}</div>
          )}
        </div>

        <h3>Required Document</h3>
        <div id="Required Documents" className="row g-3 mb-3">
          {/* Proof of Address - Always shown */}
          <DragDropUpload
            documentType="proof_of_address"
            title="Proof of Address"
            onDocumentUploaded={handleDocumentUploaded}
            onDocumentUploadFailed={handleDocumentUploadFailed}
            uploadedFilenames={uploadedFilenames}
            isUploading={uploadingDocuments.has('proof_of_address')}
            onUploadStart={handleUploadStart}
            hasError={documentErrors.has('proof_of_address')}
          />
        </div>

        {/* Navigation buttons */}
        <div className="d-flex justify-content-between mt-4">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handlePrevious}
          >
            Previous
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loadingStates.step2}
          >
            {loadingStates.step2 ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              'Next'
            )}
          </button>
        </div>

        {validationErrors.submit && (
          <div className="alert alert-danger mt-3">
            {validationErrors.submit}
          </div>
        )}
      </form>

      <style jsx>{`
        /* h3 styling to match Step 1 */
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }

        /* Document card styling to match Step 1 */
        .doc-card {
          transition: box-shadow 0.2s, transform 0.3s ease;
          animation: fadeInSlide 0.4s ease-in-out;
        }
        .doc-card:hover {
          box-shadow: 0 0 0.5rem #0d6efd33;
          background: #f8f9fa;
          transform: translateY(-2px);
        }
        
        .doc-card.clickable {
          transition: all 0.3s ease;
          user-select: none;
        }
        
        .doc-card.clickable:hover {
          box-shadow: 0 0 1rem #0d6efd44;
          background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
          transform: translateY(-3px);
          border-color: #0d6efd;
        }
        
        .doc-card.clickable:active {
          transform: translateY(-1px);
          box-shadow: 0 0 0.5rem #0d6efd33;
        }
        
        /* Success, error, and uploading states for document cards */
        .bg-success-subtle {
          background-color: rgba(25, 135, 84, 0.1) !important;
        }
        
        .border-success {
          border-color: #198754 !important;
        }
        
        .bg-danger-subtle {
          background-color: rgba(220, 53, 69, 0.1) !important;
        }
        
        .border-danger {
          border-color: #dc3545 !important;
        }
        
        .bg-info-subtle {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        .border-info {
          border-color: #0d6efd !important;
        }
        
        /* Status indicator positioning */
        .position-absolute {
          position: absolute !important;
        }
        
        .top-0 {
          top: 0 !important;
        }
        
        .end-0 {
          right: 0 !important;
        }
        
        .p-2 {
          padding: 0.5rem !important;
        }

        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* MDB5 validation styles - matching StudentFormWizard */
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .form-control.is-invalid:focus,
        .form-select.is-invalid:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #fff;
          text-align: left;
          position: relative;
          z-index: 1;
          padding: 0.5rem 0.75rem;
          background: #dc3545;
          border-radius: 0.375rem;
          border: 1px solid #dc3545;
        }
        
        /* For form containers, ensure validation message doesn't overlap */
        .form-outline .invalid-feedback,
        .form-floating .invalid-feedback {
          margin-top: 0.5rem;
          position: relative;
          clear: both;
        }
        
        /* Add arrow pointer to validation message */
        .invalid-feedback::before {
          content: '';
          position: absolute;
          top: -5px;
          left: 1rem;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 5px solid #dc3545;
        }
      `}</style>
    </div>
  );
};

export default Step2Address;
